// Decode a sample blob and register it with Strudel under a given name.
//
// One routine, two callers: the boot rehydrate path (every persisted
// sample re-registered at page load) and the community-insert path
// (a freshly downloaded cloud sample landed in the playback surface
// right before its statement is appended to the song). Centralising
// the decode + registerSound step keeps the two paths honest — the
// same sound metadata, same playback options, same pitched-root
// handling everywhere.
//
// decodeAudioData detaches the ArrayBuffer it receives (Safari/WebKit
// behaviour). Callers must pass a Blob, not a reused ArrayBuffer.

import { registerSound } from '../strudel/extensions';
import { createSamplePlayback } from './playback';

export type RegisterOptions = {
  /** The name that Strudel will look up: `s("<name>")`. superdough
   *  lowercases + underscore-normalises internally. */
  name: string;
  /** Pitched samples (synth / chop / mangle output) — MIDI root so
   *  note() / n() transposes correctly. Undefined for untuned drums. */
  pitchedRootMidi?: number;
};

/** Decode `blob` in `ctx`, then register a playback handler under
 *  `opts.name`. Returns the decoded buffer so callers can reuse it
 *  (e.g. sparkline peaks) without a second decode. Rejects if
 *  decodeAudioData fails; callers decide whether to degrade or bubble. */
export async function registerSampleBlob(
  ctx: AudioContext,
  blob: Blob,
  opts: RegisterOptions,
): Promise<AudioBuffer> {
  const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
  registerSound(
    opts.name,
    createSamplePlayback(ctx, buffer, {
      pitchedRootMidi: opts.pitchedRootMidi,
    }),
    { type: 'sample', user: true },
  );
  return buffer;
}
